import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { setToken, getToken, clearToken, setUserSnapshot, setPersistent, isPersistent } from "./tokenStore";
import { registerPushOnNative, isNative } from "./native";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setUserSnapshot(data);
      return data;
    } catch (e) {
      setUser(null);
      clearToken();
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Native push registration once authenticated
  useEffect(() => {
    if (!user || !isNative()) return;
    registerPushOnNative(async (deviceToken) => {
      try {
        const platform = /android/i.test(navigator.userAgent) ? "android" : "ios";
        await api.post("/me/device-tokens", { platform, token: deviceToken });
      } catch (e) { /* silent */ }
    });
  }, [user]);

  const login = async (email, password, keepLoggedIn = true) => {
    setPersistent(!!keepLoggedIn);
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUserSnapshot(data.user);
    setUser(data.user);
    return data.user;
  };

  const uaePassLogin = async (keepLoggedIn = true) => {
    setPersistent(!!keepLoggedIn);
    const { data } = await api.post("/auth/uae-pass", {});
    setToken(data.token);
    setUserSnapshot(data.user);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    clearToken();
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh, uaePassLogin, isPersistent: isPersistent() }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export function homeFor(role) {
  if (role === "admin" || role === "dispatcher") return "/admin";
  if (role === "driver") return "/driver";
  return "/app";
}
